import React from 'react';

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  isUnlocked: boolean;
}

interface AchievementDisplayProps {
  isActive?: boolean;
}

const ACHIEVEMENTS: Achievement[] = [
  { 
    id: 'speed-demon', 
    name: 'Speed Reader', 
    description: 'Read at 600 WPM', 
    icon: '⚡',
    isUnlocked: true
  },
  { 
    id: 'focus-master', 
    name: 'Challenge Complete', 
    description: 'Finished the speed test', 
    icon: '🎯',
    isUnlocked: true
  },
  { 
    id: 'content-explorer', 
    name: 'First Step', 
    description: 'Started your journey', 
    icon: '🚀',
    isUnlocked: true
  }
];

const AchievementDisplay: React.FC<AchievementDisplayProps> = ({ isActive = false }) => {
  if (!isActive) return null;

  return (
    <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
      {ACHIEVEMENTS.map((achievement, index) => (
        <AnimatedDiv
          key={achievement.id}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: index * 0.15 }}
          className={`
            p-4 rounded-xl text-center transition-all duration-300
            ${achievement.isUnlocked 
              ? 'bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30' 
              : 'bg-slate-200 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 opacity-50'
            }
          `}
        >
          <div className="text-3xl mb-2">{achievement.icon}</div>
          <h4 className="font-semibold text-slate-900 dark:text-white text-sm">{achievement.name}</h4>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{achievement.description}</p>
          {achievement.isUnlocked && (
            <div className="mt-2 text-amber-600 dark:text-amber-400 text-xs font-medium">
              ✓ Unlocked!
            </div>
          )}
        </AnimatedDiv>
      ))}
    </div>
  );
};

// Simple animation component since we're not using framer-motion
const AnimatedDiv: React.FC<{
  children: React.ReactNode;
  initial: { scale: number; opacity: number };
  animate: { scale: number; opacity: number };
  transition?: { delay: number };
  className?: string;
}> = ({ children, initial, animate, transition, className }) => {
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, (transition?.delay || 0) * 1000);
    return () => clearTimeout(timer);
  }, [transition?.delay]);

  return (
    <div
      className={className}
      style={{
        transform: isVisible ? `scale(${animate.scale})` : `scale(${initial.scale})`,
        opacity: isVisible ? animate.opacity : initial.opacity,
        transition: 'transform 0.5s ease-out, opacity 0.5s ease-out',
        transitionDelay: `${transition?.delay || 0}s`
      }}
    >
      {children}
    </div>
  );
};

export default AchievementDisplay;
